import { useMemo } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

import { Button } from "@covertree/ui";
import {
  AchPaymentMethod,
  CreditCardPaymentMethod,
} from "@covertree/agent-portal-graphql";
import { PaymentMethodItem } from "./PaymentMethodItem";
import { useRemoveStripePaymentMethod } from "./useRemoveStripePaymentMethod";
import { useChangeDefaultStripePaymentMethod } from "./useChangeDefaultStripePaymentMethod";
import { RemoveCreditCardModal } from "./RemoveCreditCardModal";
import { PaymentMethodsStripeUpdateDisclaimer } from "../PaymentMethodsCardUpdateDisclaimer";

interface Props {
  cards?: CreditCardPaymentMethod[];
  achMethods?: AchPaymentMethod[];
  policyLocator: string;
  onClose(): void;
  onAddNewPaymentMethod(): void;
  shouldTriggerRenewalReissuance: boolean;
  renewalLocator?: string;
}

export function PaymentMethodsList({
  cards,
  achMethods,
  policyLocator,
  onClose,
  onAddNewPaymentMethod,
  shouldTriggerRenewalReissuance,
  renewalLocator,
}: Props) {
  const allMethods = useMemo(() => {
    const combined: (CreditCardPaymentMethod | AchPaymentMethod)[] = [
      ...(cards || []),
      ...(achMethods || []),
    ];
    return combined;
  }, [cards, achMethods]);

  const defaultPaymentMethod = useMemo(
    () => allMethods.find((method) => method.default),
    [allMethods]
  );

  const {
    methodToRemove,
    setMethodToRemove,
    onRemoveMethodModalClose,
    removeMethod,
    loading: isRemoveMethodLoading,
  } = useRemoveStripePaymentMethod(policyLocator);

  const {
    selectedPaymentMethodId,
    onUpdateDefaultPaymentMethod,
    setSelectedPaymentMethodId,
    loading: isChangingPaymentMethod,
  } = useChangeDefaultStripePaymentMethod(
    policyLocator,
    defaultPaymentMethod?.id,
    shouldTriggerRenewalReissuance
  );

  return (
    <>
      <Box>
        {!allMethods.length && (
          <Typography>No payment methods found.</Typography>
        )}
        {allMethods.length > 0 && (
          <Stack spacing={1}>
            {allMethods.map((method) => (
              <PaymentMethodItem
                key={method.id}
                method={method}
                onClick={() => {
                  setSelectedPaymentMethodId(method.id);
                }}
                onDelete={() => {
                  setMethodToRemove(method);
                }}
                selected={method.id === selectedPaymentMethodId}
                disabled={isChangingPaymentMethod}
              />
            ))}
          </Stack>
        )}
        <Box mt={2}>
          <Button
            variant="outlined"
            size="small"
            disabled={isChangingPaymentMethod}
            onClick={onAddNewPaymentMethod}>
            Add New Payment Method
          </Button>
        </Box>
        {shouldTriggerRenewalReissuance &&
          defaultPaymentMethod?.id !== selectedPaymentMethodId && (
            <PaymentMethodsStripeUpdateDisclaimer
              renewalLocator={renewalLocator}
            />
          )}
        <Stack mt={4} direction="row" spacing={1}>
          {!!allMethods.length && (
            <Button
              loading={isChangingPaymentMethod}
              disabled={
                isChangingPaymentMethod ||
                defaultPaymentMethod?.id === selectedPaymentMethodId
              }
              onClick={onUpdateDefaultPaymentMethod}>
              Update Payment Method
            </Button>
          )}
          <Button
            variant="outlined"
            disabled={isChangingPaymentMethod}
            onClick={onClose}>
            Close
          </Button>
        </Stack>
      </Box>
      <RemoveCreditCardModal
        method={methodToRemove}
        isLoading={isRemoveMethodLoading}
        onClose={onRemoveMethodModalClose}
        onRemove={removeMethod}
      />
    </>
  );
}

export default PaymentMethodsList;
